import type { WorldSeenCursors } from "./worldUnseenSettle";

// NV8 — the resolver-level merge, and nothing more. The fold keeps its one
// law (worldUnseenSettle.ts, byte-identical); this module only answers which
// cursor stands for each World door before the fold runs: the most recent
// look wins, whether it happened on this device or anywhere else the Actor
// stood. Local stays device-only — it is ungoverned, no World row owns it.
// Absence stays typed: a door with neither custody resolves to no cursor,
// and the fold then shows nothing (never invented into a marker).

/** Actor-level cursors by worldKey, epoch ms, as the World doors report them. */
export type ActorSeenCursors = Readonly<Record<string, number>>;

export const EMPTY_ACTOR_SEEN_CURSORS: ActorSeenCursors = {};

/**
 * Merge the Actor-level cursors into the device-local cursors,
 * most-recent-cursor-wins per World door. Output speaks the fold's input
 * grammar (ISO-8601), so the fold never learns a second custody existed.
 */
export function resolveSeenCursors(
  device: WorldSeenCursors,
  actor: ActorSeenCursors,
): WorldSeenCursors {
  const worldKeys = new Set([...Object.keys(device.byWorldKey), ...Object.keys(actor)]);
  if (worldKeys.size === 0) return device;
  const byWorldKey: Record<string, string> = {};
  for (const worldKey of worldKeys) {
    const deviceIso = device.byWorldKey[worldKey];
    // An unparseable device stamp yields NaN and every comparison below is
    // false, so a real Actor cursor stands in for it — same guess-refusal
    // the fold applies, resolved instead of marked.
    const deviceAt = deviceIso === undefined ? Number.NaN : Date.parse(deviceIso);
    const actorAt = actor[worldKey];
    if (actorAt !== undefined && !(deviceAt >= actorAt)) {
      byWorldKey[worldKey] = new Date(actorAt).toISOString();
    } else if (deviceIso !== undefined) {
      byWorldKey[worldKey] = deviceIso;
    }
  }
  return { byWorldKey, local: device.local };
}
